import { useCallback, useMemo } from "react";

import { useAuthContext } from "../auth";
import { Breadcrumb } from "../types/breadcrumbs";
import { getHashedString, GoogleAnalyticsEvent, LoginStatus } from "../utils/analytics";
import { getCountryLanguageFromLocaleCode } from "../utils/i18n";
import { DataLayerEvent, useDataLayer } from "./use-data-layer";

export interface TrackPageviewProps {
  breadcrumbs: Breadcrumb[] | null;
  locale: string | null;
  content_group: string | null;
}

export interface PageviewEventProps extends DataLayerEvent {
  event: GoogleAnalyticsEvent.DlStart;
  breadcrumbs: string | null;
  login_status: string;
  country: string | null;
  page_language: string | null;
  content_group: string | null;
  user_email_md5: string | null;
  user_email_sha256: string | null;
  user_id: string | null;
}

export function useTrackPageview() {
  const { pushEvent } = useDataLayer();
  const authContextValue = useAuthContext();
  const { logged, userInfo } = useMemo(() => {
    return authContextValue ?? {};
  }, [authContextValue]);

  const trackPageview = useCallback(
    ({ breadcrumbs, locale, content_group }: TrackPageviewProps) => {
      const hashedUserEmail = userInfo?.email ? getHashedString(userInfo.email) : null;
      const { country, language } = locale
        ? getCountryLanguageFromLocaleCode(locale)
        : { country: null, language: null };

      const event: PageviewEventProps = {
        event: GoogleAnalyticsEvent.DlStart,
        breadcrumbs:
          breadcrumbs?.map((breadcrumb) => breadcrumb.defaultLocaleLabel).join(" | ") ?? null,
        login_status: logged ? LoginStatus.Logged : LoginStatus.NotLogged,
        country,
        page_language: language,
        content_group,
        user_email_md5: hashedUserEmail?.md5 ?? null,
        user_email_sha256: hashedUserEmail?.sha256 ?? null,
        user_id: userInfo?.sub ?? null,
      };
      pushEvent(event, ["user_id"]);
    },
    [pushEvent, logged, userInfo]
  );

  return trackPageview;
}
